import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { PageRoot, PageHeader, Toolbar } from '../ui/Page';
import { SearchInput } from '../ui/SearchInput';
import { DataTable, dash, type DataTableColumn } from '@/components/ui/data-table';
import { useServerTable } from '../ui/useServerTable';
import { StatusBadge } from '../ui/Badge';
import { Sheet, SheetOpenFull } from '../ui/Sheet';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState } from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import { Button } from '../ui/Button';
import { NewCustomerSheet } from '../create/NewCustomerSheet';
import { IconCustomers, IconPlus } from '../icons';

type CustomerRow = {
  id: string;
  kind: string;
  displayName: string;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  billingCity: string | null;
  billingState: string | null;
  createdAt: string | Date;
};

const columns: DataTableColumn<CustomerRow>[] = [
  {
    key: 'name',
    label: 'Name',
    filterable: true,
    value: (c) => c.displayName,
    // Match the server's allow-list (displayName OR companyName) so the two modes never
    // answer the same keystroke differently.
    searchValue: (c) => `${c.displayName} ${c.companyName ?? ''}`,
    cell: (c) => (
      <div>
        <p className="font-medium">{c.displayName}</p>
        {c.companyName && c.companyName !== c.displayName ? (
          <p className="text-xs text-muted-foreground">{c.companyName}</p>
        ) : null}
      </div>
    ),
  },
  {
    key: 'kind',
    label: 'Type',
    filterable: true,
    value: (c) => c.kind,
    cell: (c) => (
      <StatusBadge tone={c.kind === 'commercial' ? 'brand' : 'neutral'}>
        {c.kind}
      </StatusBadge>
    ),
  },
  {
    key: 'email',
    label: 'Email',
    filterable: true,
    value: (c) => c.email,
    cell: (c) => dash(c.email),
  },
  {
    key: 'phone',
    label: 'Phone',
    filterable: true,
    value: (c) => c.phone,
    cell: (c) => dash(c.phone),
  },
  {
    key: 'city',
    label: 'City',
    filterable: true,
    value: (c) => c.billingCity,
    // The cell prints "City, ST" and the server matches city OR state — search the same.
    searchValue: (c) => `${c.billingCity ?? ''} ${c.billingState ?? ''}`,
    cell: (c) =>
      dash(
        c.billingCity
          ? `${c.billingCity}${c.billingState ? ', ' + c.billingState : ''}`
          : null,
      ),
  },
  {
    // `created`, matching CUSTOMER_SORT — as `added` this was the one no-op sort the
    // census counted on this page: the header cycled aria-sort while the allow-list
    // silently dropped the key and the fallback order came back.
    key: 'created',
    label: 'Added',
    // Sort chronologically on epoch millis, but search only what the user can see —
    // otherwise typing "2026" matches an invisible timestamp and inflates "N of M".
    value: (c) => new Date(c.createdAt).getTime(),
    searchValue: (c) => new Date(c.createdAt).toLocaleDateString(),
    cell: (c) => new Date(c.createdAt).toLocaleDateString(),
    align: 'right',
  },
];

export function CustomersPage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { search, setSearch, query, table } = useServerTable();
  const customersQuery = useQuery({
    ...trpc.customer.list.queryOptions(query),
    // Hold the previous page on screen while the next one loads, so typing doesn't
    // flash the table away and back on every keystroke.
    placeholderData: (prev) => prev,
  });
  const [preview, setPreview] = useState<CustomerRow | null>(null);
  const [creating, setCreating] = useState(false);

  // Three honest states: pending (isPending also covers offline-paused), failed, real.
  // A failed load must never render as "no customers yet" — see ui/QueryStates.
  if (customersQuery.isPending) return <PageSkeleton />;
  if (customersQuery.isError) {
    return (
      <PageRoot>
        <PageHeader title="Customers" />
        <ErrorState
          icon={IconCustomers}
          what="customers"
          onRetry={() => void customersQuery.refetch()}
        />
      </PageRoot>
    );
  }
  const rows: CustomerRow[] = customersQuery.data.rows;

  const openFull = (id: string) =>
    navigate({ to: '/customers/$customerId', params: { customerId: id } });

  return (
    <PageRoot>
      <PageHeader
        title="Customers"
        right={
          // No "Message clients" here any more (MAP 249): bulk messages are switched off, and
          // customer email is the automatic emails a shop's own work triggers.
          <Button onClick={() => setCreating(true)}>
            <IconPlus size={15} />
            New customer
          </Button>
        }
      />
      <Toolbar>
        <SearchInput
          placeholder="Search customers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search customers"
        />
      </Toolbar>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(c) => c.id}
        search={search}
        tableId="customers"
        server={table(customersQuery.data.total, customersQuery.isFetching)}
        onRowClick={(c) => setPreview(c)}
        rowLabel={(c) => `Preview ${c.displayName}`}
        empty={
          <EmptyState
            icon={IconCustomers}
            title="No customers yet"
            description="Add your first customer, or import your list from a CSV."
          />
        }
      />
      <Sheet
        open={preview !== null}
        onClose={() => setPreview(null)}
        title={preview?.displayName ?? ''}
        badge={
          preview ? (
            <StatusBadge tone={preview.kind === 'commercial' ? 'brand' : 'neutral'}>
              {preview.kind}
            </StatusBadge>
          ) : null
        }
        footer={preview ? <SheetOpenFull onClick={() => openFull(preview.id)} /> : null}
      >
        {preview ? (
          <dl className="space-y-3 text-sm">
            {(
              [
                ['Company', preview.companyName],
                ['Email', preview.email],
                ['Phone', preview.phone],
                [
                  'Billing city',
                  preview.billingCity
                    ? `${preview.billingCity}${preview.billingState ? ', ' + preview.billingState : ''}`
                    : null,
                ],
                ['Added', new Date(preview.createdAt).toLocaleDateString()],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between gap-4">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="text-right font-medium">{dash(value)}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </Sheet>
      <NewCustomerSheet
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={openFull}
      />
    </PageRoot>
  );
}
